import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ScrollText, Tag } from "lucide-react";
import api from "@/lib/api";

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

export default function FeePreviewDialog({ open, onOpenChange, purpose, price, locality = "", onConfirm, submitting }) {
  const [q, setQ] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ(null);
    api
      .get("/fees/quote", { params: { purpose, price: parseFloat(price) || 0, locality } })
      .then((r) => setQ(r.data))
      .catch(() => setQ(null));
  }, [open, purpose, price, locality]);

  if (!open) return null;
  if (!q) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md"><div className="p-6 text-slate-500 text-sm">Calculating fee...</div></DialogContent>
      </Dialog>
    );
  }

  const purposeLabel = { sale: "Sale", rent: "Rent", lease: "Lease" }[purpose] || purpose;
  const hasDiscount = q.discount_pct > 0 && q.promotion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden" data-testid="fee-preview-dialog">
        <div className="bg-kp-teal p-5 text-white">
          <DialogTitle className="font-display text-xl flex items-center gap-2"><ScrollText size={20}/> Fee Preview — {purposeLabel}</DialogTitle>
          <p className="text-white/85 text-sm mt-1">Please review the platform fee before publishing your listing.</p>
        </div>
        <div className="p-5 space-y-3">
          {hasDiscount && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="fee-promo-banner">
              <Tag size={16} className="mt-0.5 shrink-0"/>
              <div>
                <b>{q.discount_pct}% Off applied</b> for {locality} listings{q.promotion.note ? ` — ${q.promotion.note}` : ""}.
                <div className="text-xs text-emerald-700 mt-0.5">Valid till {q.promotion.valid_till}</div>
              </div>
            </div>
          )}
          <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y">
            <Row k={`Your Base ${purpose === "sale" ? "Price" : "Rent"}`} v={inr(parseFloat(price) || 0)}/>
            <Row k={`Platform Fee (${q.label})`} v={inr(q.base_fee)}/>
            {hasDiscount && <Row k={`Promotional Discount (${q.discount_pct}%)`} v={`- ${inr(q.discount_amount)}`} strike/>}
            {hasDiscount && <Row k="Net Platform Fee" v={inr(q.net_fee)}/>}
            <Row k={`GST (${q.gst_pct}%)`} v={inr(q.gst_amount)}/>
            <Row k="Total Fees" v={inr(q.total)} bold/>
          </div>
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg p-3">
            <b>Note:</b> This fee structure is based on the base price you entered. The final amount will be applicable on the actual deal amount at closing.
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full" data-testid="fee-cancel">Cancel</Button>
            <Button onClick={onConfirm} disabled={submitting} className="btn-primary flex-1 rounded-full font-semibold flex items-center justify-center gap-1" data-testid="fee-confirm">
              <CheckCircle2 size={16}/> {submitting ? "Submitting..." : "Confirm & Post"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, bold, strike }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 text-sm ${bold ? "text-kp-maroon font-bold text-base" : "text-slate-700"} ${strike ? "text-emerald-700" : ""}`}>
      <span>{k}</span><span>{v}</span>
    </div>
  );
}
